import { lazy, Suspense, useState } from 'react';
import type { InternalMemoFormData } from '../../types/applicationForm';
import { MemoAiDraftModal } from './MemoAiDraftModal';

const MemoBodyEditor = lazy(() =>
  import('./MemoBodyEditor').then((m) => ({ default: m.MemoBodyEditor })),
);

type Props = {
  value: InternalMemoFormData;
  onChange: (next: InternalMemoFormData) => void;
  applicationNo?: string;
  /** Exclude this submission when picking AI style references. */
  memoId?: string;
  disabled?: boolean;
};

export function InternalMemoForm({
  value,
  onChange,
  applicationNo,
  memoId,
  disabled = false,
}: Props) {
  const [aiOpen, setAiOpen] = useState(false);
  const patch = (partial: Partial<InternalMemoFormData>) => onChange({ ...value, ...partial });

  return (
    <div className={`memo-form${disabled ? ' is-readonly' : ''}`}>
      <header className="memo-form__header">
        <p className="memo-form__ref">
          Ref no: <strong>{applicationNo || value.ref_no || 'Assigned on save'}</strong>
        </p>
      </header>

      <div className="memo-form__grid">
        <label className="field">
          <span className="field-label">To</span>
          <input
            className="field-input"
            value={value.to}
            disabled={disabled}
            required
            placeholder="Enter recipient"
            onChange={(e) => patch({ to: e.target.value })}
          />
        </label>
        <label className="field">
          <span className="field-label">CC</span>
          <input
            className="field-input"
            value={value.cc}
            disabled={disabled}
            placeholder="Enter CC (optional)"
            onChange={(e) => patch({ cc: e.target.value })}
          />
        </label>
        <label className="field">
          <span className="field-label">From</span>
          <input
            className="field-input field-input--readonly"
            value={value.issued_by}
            disabled
            readOnly
            placeholder="Assigned from creator"
          />
        </label>
        <label className="field">
          <span className="field-label">Date</span>
          <input
            type="date"
            className="field-input"
            value={value.date}
            disabled={disabled}
            required
            onChange={(e) => patch({ date: e.target.value })}
          />
        </label>
      </div>

      <div className="memo-form__subject-row">
        <label className="field memo-form__subject-field">
          <span className="field-label">Subject</span>
          <input
            className="field-input"
            value={value.subject}
            disabled={disabled}
            required
            placeholder="Subject of the memorandum"
            onChange={(e) => patch({ subject: e.target.value })}
          />
        </label>
        {!disabled ? (
          <button
            type="button"
            className="btn-secondary btn-sm memo-form__ai-btn"
            onClick={() => setAiOpen(true)}
          >
            AI draft
          </button>
        ) : null}
      </div>

      <div className="field">
        <span className="field-label">Body</span>
        <Suspense
          fallback={
            <div className="memo-body-editor memo-body-editor--loading">
              <div className="memo-body-editor__surface" />
            </div>
          }
        >
          <MemoBodyEditor
            value={value.body}
            disabled={disabled}
            onChange={(body) => patch({ body })}
          />
        </Suspense>
      </div>

      <MemoAiDraftModal
        open={aiOpen && !disabled}
        onClose={() => setAiOpen(false)}
        to={value.to}
        cc={value.cc}
        from={value.issued_by}
        date={value.date}
        currentSubject={value.subject}
        currentBody={value.body}
        excludeMemoId={memoId}
        onApply={({ subject, bodyHtml }) => patch({ subject, body: bodyHtml })}
      />
    </div>
  );
}
